/**
 * The ShopProduct container
 */
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Page from "../../components/Page";
import { showProductAndLoadCMSBlocks } from "../../redux/shop/products";
import shopProductSelectors from "../../redux/shop/products/selectors";
import { addItemToCart, resetCartState } from "../../redux/shop/cart";
import { COLORTYPES, COLORS } from "../../utils/colors";
import LoadingContent from "../../components/LoadingContent";
import InproductConfigurator from "../../components/InproductConfigurator";
import { Container, Row, Col, Button, Table } from "reactstrap";
import {
  Nav,
  NavItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { reset as resetFileUpload } from "../../redux/editor/file";
import CMSBlock from "../../components/CMSBlock";
import AddToCartModal from "../../components/CheckoutCart/utils/AddToCartModal";
import FourOFour from "../Utils/FourOFour";
import DataLayerHelper from "../../utils/dataLayer";
import { resetCookieError } from "../../redux/shop/errors";
import { FaTimes } from "react-icons/fa";
import { FILEEDITOR } from "../../components/FileUploadEditorForm/utils/fileEditor";
import { useSsrEffect } from "@issr/core";
import { isServer } from "../../redux/store";
import {
  REACT_APP_WEB_SERVER,
  REACT_APP_SHOP_CDN_SERVER,
} from "../../utils/env";
import { push } from "connected-react-router";
import Status from "../Utils/Status";
import RelatedProducts from "../../components/ShopProduct/RelatedProducts";
import FaqsAccordion from "../../components/FaqsListView/utils/FaqsAccordion";
import Sticky from "react-sticky-el";
import { useIsVisible } from "../../hooks/useIsVisible";
import BreadcrumbBar from "../../components/Utilities/BreadcrumbBar/BreadcrumbBar";
import MoreColorsTeaser from "../../components/ShopProduct/utils/MoreColorsTeaser";
import ShippingOptions from "../../components/ShopProduct/utils/ShippingOptions";
import VolumeDiscountTable from "../../components/ShopProduct/utils/VolumeDiscountTable";
import ShopProductImageDisplay from "../../components/ShopProduct/utils/ShopProductImageDisplay";
import ProductCustomization from "../../components/ShopProduct/utils/ProductCustomization";
import ProductVariantSelector from "../../components/ShopProduct/utils/ProductVariantSelector";
import PurchaseButton from "../../components/ShopProduct/utils/PurchaseButton";
/**
 * Frontload function called before component mounts to
 * prefetch the shopProduct we want to show.
 * It looks in the url for an ShopProduct id parameter to load the resource
 */

/**
 * The `show` container for an ShopProduct.
 * Both the component and the connected component are exported for testing purpose
 */
const ShopProduct = (props, context) => {
  const {
    loadingShow,
    language,
    cart,
    availableCmsBlocks,
    addToCartProcessing,
    addToShopCartSuccess,
    addToCartError,
    showFourOFour,
    errorRedirect,
    cookieError,
    faqDictionary,
  } = useSelector((state) => ({
    availableCmsBlocks: state.cmsblocks.list,
    loadingShow: state.shopProducts.showLoading,
    showFourOFour: state.shopProducts.showFourOFour,
    errorRedirect: state.shopProducts.errorRedirect,
    loadedShow: state.shopProducts.loadedShow,
    language: state.i18nState.lang,
    addToShopCartSuccess: state.shopCart.addToShopCartSuccess,
    addToCartProcessing: state.shopCart.addToCartProcessing,
    addToCartError: state.shopCart.error,
    cookieError: state.shopErrors.cookieError,
    cart: state.shopCart.cart,
    faqDictionary: state.faqs.dictionary,
  }));

  const [amount, setAmount] = useState(1);
  const [selectedShippingCountry, setSelectedShippingCountry] =
    useState("germany");
  const [materialSerie, setMaterialSerie] = useState(1);
  const [selectedColor, setSelectedColor] = useState({
    value: COLORS[1][0],
    label: COLORS[1][0].title,
  });
  const [custom_size, setCustomSize] = useState({});
  const [currentSlug, setCurrentSlug] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const shopProductSlug = props.match.params.slug;
  const productDebug = useSelector((state) =>
    shopProductSelectors.selectProductBySlug(state, language, shopProductSlug)
  );

  const {
    shopProductCmsBlocks,
    shopProductData,
    shopProductDataAttributes,
    shopProductDataRelationships,
    shopProductIncluded,
  } = {
    shopProductCmsBlocks: productDebug?.data?.attributes?.cms_blocks,
    shopProductData: productDebug?.data,
    shopProductDataAttributes: productDebug?.data?.attributes,
    shopProductDataRelationships: productDebug?.data?.relationships,
    shopProductIncluded: productDebug?.included,
  };

  const presetFromUrl = new URLSearchParams(props.location.search); // editorType = product
  const presetVariant = presetFromUrl.get("variant");
  const skipdc = presetFromUrl.get("skipdc");
  const dispatch = useDispatch();

  const frontload = async () => {
    // FIXME 404 -> redirect?
    // FIXME check for language
    // console.log(
    //   "shopProductDataAttributes",
    //   shopProductDataAttributes,
    //   language
    // );
    if (shopProductSlug !== currentSlug) {
      setCurrentSlug(shopProductSlug);
      dispatch(resetFileUpload());
    }
    if (
      (!shopProductDataAttributes ||
        shopProductDataAttributes?.slug !== shopProductSlug ||
        !shopProductIncluded) &&
      !loadingShow
    ) {
      dispatch(
        showProductAndLoadCMSBlocks({
          slug: shopProductSlug,
          language: language,
          successCallback: (productData) => {
            if (presetVariant) {
              const newSelectedVariant = productData?.included?.find((obj) => {
                return (
                  obj.attributes.sku === presetVariant && obj.type === "variant"
                );
              });

              if (newSelectedVariant) {
                setSelectedVariant(newSelectedVariant);
                setSelectedOptions(
                  newSelectedVariant.relationships?.option_values?.data?.map(
                    (optVal) =>
                      productData?.included?.find((obj) => {
                        return (
                          obj.id === optVal?.id && obj.type === "option_value"
                        );
                      })
                  )
                );
              }
            } else {
              setSelectedVariant(null);
              setSelectedOptions([]);
            }
          },
        })
      );
    }
  };

  useSsrEffect(frontload, [
    shopProductSlug,
    language,
    loadingShow,
    shopProductDataAttributes,
    shopProductIncluded,
    showProductAndLoadCMSBlocks,
    presetVariant,
  ]);

  const inpageConfiguratorRef = useRef(null);
  const scrollToConfigurator = () => {
    if (inpageConfiguratorRef && inpageConfiguratorRef.current)
      inpageConfiguratorRef.current.scrollIntoView({ behaviour: "smooth" });
  };
  const buyBoxRef = useRef(null);
  const scrollToBuySection = () => {
    if (buyBoxRef && buyBoxRef.current)
      buyBoxRef.current.scrollIntoView({ behaviour: "smooth" });
  };

  const descriptionRef = useRef(null);
  const installationRef = useRef(null);
  const specificationRef = useRef(null);
  const materialRef = useRef(null);
  const faqRef = useRef(null);
  const visibleRefOptions = {
    rootMargin: `-120px 0px 0px 0px`, // Top offset to trigger visibility
  };
  const descriptionRefIsVisible = useIsVisible(
    descriptionRef,
    visibleRefOptions
  );
  const specificationRefIsVisible = useIsVisible(
    specificationRef,
    visibleRefOptions
  );
  const installationRefIsVisible = useIsVisible(
    installationRef,
    visibleRefOptions
  );
  const materialRefIsVisible = useIsVisible(materialRef, visibleRefOptions);
  const faqRefIsVisible = useIsVisible(faqRef, visibleRefOptions);

  // Store all visibility states in an array to calculate the currently visible section
  const sections = [
    {
      ref: descriptionRef,
      isVisible: descriptionRefIsVisible,
      id: "description",
    },
    {
      ref: installationRef,
      isVisible: installationRefIsVisible,
      id: "installation",
    },
    { ref: materialRef, isVisible: materialRefIsVisible, id: "material" },
    {
      ref: specificationRef,
      isVisible: specificationRefIsVisible,
      id: "specification",
    },
    { ref: faqRef, isVisible: faqRefIsVisible, id: "faq" },
  ];

  // Find the first visible section or the section closest to the top
  const activeSection =
    sections.find((section) => section.isVisible)?.id || null;

  const scrollToSection = (sectionName) => {
    if (sectionName === "description") {
      if (descriptionRef && descriptionRef.current)
        descriptionRef.current.scrollIntoView({ behaviour: "smooth" });
    } else if (sectionName === "installation") {
      if (installationRef && installationRef.current)
        installationRef.current.scrollIntoView({ behaviour: "smooth" });
    } else if (sectionName === "specification") {
      if (specificationRef && specificationRef.current)
        specificationRef.current.scrollIntoView({ behaviour: "smooth" });
    } else if (sectionName === "material") {
      if (materialRef && materialRef.current)
        materialRef.current.scrollIntoView({ behaviour: "smooth" });
    } else if (sectionName === "faq") {
      if (faqRef && faqRef.current)
        faqRef.current.scrollIntoView({ behaviour: "smooth" });
    }
  };

  const configuratorPage =
    shopProductDataAttributes?.product_page_type === "inpage_configurator";
  // Helpers
  const defaultVariant = shopProductIncluded
    ? shopProductIncluded?.find((obj) => {
        return obj.attributes.sku === presetVariant && obj.type === "variant";
      })
    : null;

  const shopProductFaqSlugs =
    shopProductDataAttributes?.faq_slugs !== ""
      ? shopProductDataAttributes?.faq_slugs?.split(",")
      : null;

  const handleMaterialChange = (e) => {
    const newMaterial = e.target.value;
    if (COLORS[newMaterial].length) {
      var firstColorForNewMaterial = COLORS[newMaterial][0];
      setMaterialSerie(newMaterial);
      setSelectedColor({
        value: firstColorForNewMaterial,
        label: firstColorForNewMaterial.title,
      });
    }
  };

  const handleColorChange = (e) => {
    setSelectedColor(e);
  };

  const getCustomPrice = () => {
    let price = 0;
    if (custom_size.custom_width && custom_size.custom_height) {
      let itemPrice = 8.21; // fallback
      let sqmPrice = 40; // fallback
      let minPrice = 3.99; // fallback
      const colorSelectAvailable = shopProductIncluded?.find((obj) => {
        return (
          obj.type === "option_type" &&
          obj.attributes.name === "custom_colorselect"
        );
      });
      if (
        (shopProductDataAttributes.myfolie_configurator_id === 2 ||
          colorSelectAvailable) &&
        selectedColor.value
      ) {
        sqmPrice = selectedColor.value.pricePerSqm;
        itemPrice = 5.21;
      } else if (shopProductDataAttributes.myfolie_configurator_id > 0) {
        const fileEditorData =
          FILEEDITOR[shopProductDataAttributes.myfolie_configurator_id];
        if (fileEditorData) {
          itemPrice = fileEditorData.basicAmount * 1.1;
          sqmPrice = fileEditorData.pricePerSqm * 1.1;
          if (fileEditorData.minPrice) minPrice = fileEditorData.minPrice;
        }
      }

      price =
        itemPrice +
        sqmPrice *
          (((parseInt(custom_size.custom_width * 10) / 10) *
            (parseInt(custom_size.custom_height * 10) / 10)) /
            10000);
      price = price * 1.19;
      price = Math.floor(price * 10) / 10;
      if (minPrice > price) price = minPrice * 1.19;
    }
    return price;
  };

  const handleCustomSizeChange = (e) => {
    if (
      !e.custom_width &&
      ["custom_width", "custom_height"].includes(e.target.name)
    ) {
      const minSize = shopProductIncluded?.find((obj) => {
        return (
          obj.type === "variant" &&
          obj.attributes.options_text.indexOf("min") !== -1
        );
      });
      const customSize = custom_size;
      customSize[e.target.name] = e.target.value;
      if (minSize) {
        if (e.target.name === "custom_width") {
          customSize.custom_height =
            Math.round(
              ((e.target.value * minSize.attributes.height) /
                minSize.attributes.width) *
                10
            ) / 10;
        } else {
          customSize.custom_width =
            Math.round(
              ((e.target.value * minSize.attributes.width) /
                minSize.attributes.height) *
                10
            ) / 10;
        }
      }
      setCustomSize({ ...customSize });
    } else {
      setCustomSize(e);
    }
  };

  const handleAddItemToCart = () => {
    const currentVariantId = selectedVariant
      ? selectedVariant.id
      : defaultVariant
      ? defaultVariant.id
      : shopProductDataRelationships.default_variant.data.id;

    dispatch(
      addItemToCart({
        variant_id: currentVariantId,
        quantity: amount,
      })
    );
  };

  const addCustomItemToCart = () => {
    const currentVariantId = selectedVariant
      ? selectedVariant.id
      : defaultVariant
      ? defaultVariant.id
      : shopProductDataRelationships.default_variant.data.id;
    const currentVariant =
      selectedVariant ||
      shopProductIncluded?.find((obj) => {
        return obj.id === currentVariantId && obj.type === "variant";
      });
    const currentLaminate = shopProductIncluded?.find((obj) => {
      return (
        obj.type === "option_value" &&
        parseInt(obj.relationships.option_type.data.id) === 4 &&
        currentVariant.relationships.option_values.data
          .map((objj) => parseInt(objj.id))
          .includes(parseInt(obj.id))
      );
    }); // option_type.id 4 = laminate
    const currentLanguage = shopProductIncluded?.find((obj) => {
      return (
        obj.type === "option_value" &&
        parseInt(obj.relationships.option_type.data.id) === 12 &&
        currentVariant.relationships.option_values.data
          .map((objj) => parseInt(objj.id))
          .includes(parseInt(obj.id))
      );
    }); // option_type.id 12 = language
    const customSizeAvailable = shopProductIncluded?.find((obj) => {
      return (
        obj.type === "option_type" && obj.attributes.name === "custom_size"
      );
    });
    const colorSelectAvailable = shopProductIncluded?.find((obj) => {
      return (
        obj.type === "option_type" &&
        obj.attributes.name === "custom_colorselect"
      );
    });
    if (
      customSizeAvailable &&
      !(custom_size.custom_width && custom_size.custom_height)
    ) {
      alert("Please enter a size");
      return;
    }
    if (colorSelectAvailable && !selectedColor.value) {
      alert("Please select a color");
      return;
    }

    const rand = () => Math.random(0).toString(36).substr(2);
    const token = (length) =>
      (rand() + rand() + rand() + rand()).substr(0, length);
    const project_uid = "shop_" + token(20);

    const selectedMaterials = Object.keys(COLORTYPES).find(
      (key) => COLORTYPES[key] === parseInt(materialSerie)
    );

    let description = "";
    if (
      !customSizeAvailable &&
      currentVariant.attributes &&
      currentVariant.attributes.options_text
    )
      description += currentVariant.attributes.options_text;
    if (colorSelectAvailable)
      description += `, ${context.t("Color")}: ${
        selectedColor.label
      } (${context.t(`${selectedMaterials}`)})`;
    if (currentLaminate)
      description += `, ${context.t("Laminate")}: ${
        currentLaminate.attributes.presentation
      }`;
    if (currentLanguage)
      description += `, ${context.t("Language")}: ${
        currentLanguage.attributes.presentation
      }`;
    if (customSizeAvailable)
      description += `, ${context.t("Width")}: ${
        custom_size.custom_width
      } cm ${context.t("and")} ${context.t("Height")}: ${
        custom_size.custom_height
      } cm`;

    if (description.startsWith(", ")) description = description.substring(2);
    dispatch(
      addItemToCart({
        variant_id: currentVariantId,
        quantity: amount,
        options: {
          project_uid: project_uid,
          project_meta: JSON.stringify({
            title: context.t("Custom Options"),
            description: description,
            width: customSizeAvailable
              ? custom_size.custom_width
              : currentVariant.width,
            height: customSizeAvailable
              ? custom_size.custom_height
              : currentVariant.height,
            color: selectedColor.value,
            uid: project_uid,
            price: customSizeAvailable
              ? getCustomPrice()
              : currentVariant.attributes.price,
          }), //, FIXME not sure if this really is the way to go? in the end we want a custom option, yes but do we want this to be passed in frontend? or maybe we load this via api to api request?
          //        design_price: ? // FIXME not sure if we should pass this here or maybe load it via an api to api request?
          // FIXME validity for price?
        },
      })
    );
  };

  const updateAmount = (event) => {
    setAmount(parseInt(event.target.value));
  };

  const changeProductOption = (option, event) => {
    // remove old selection for option_type
    const newOptions = (
      selectedOptions.length > 0
        ? selectedOptions
        : defaultVariant
        ? shopProductIncluded.filter(
            (obj) =>
              obj.type === "option_value" &&
              defaultVariant.relationships.option_values.data
                .map((ovobj) => ovobj.id)
                .includes(obj.id)
          )
        : []
    ).filter((obj) => {
      return (
        obj.relationships.option_type.data.id !==
        option.relationships.option_type.data.id
      );
    });
    newOptions.push(option);
    const newOptionsIds = newOptions.map((opt) => opt.id);

    const newVariant = shopProductIncluded
      ?.filter((obj) => {
        return obj.type === "variant";
      })
      .find((obj) => {
        let is_match = true;

        const variantOptionsIds = obj.relationships.option_values.data.map(
          (opt) => opt.id
        );

        is_match = newOptionsIds.every((i) => variantOptionsIds.includes(i));
        // newOptions.map((option) => {
        //   if (
        //     !obj.relationships.option_values.data.find((v_option) => {
        //       return (
        //         option.id === v_option.id && v_option.type === "option_value"
        //       );
        //     })
        //   ) {
        //     console.log("DEBUG", "#02", "false");
        //     matches_all = false;
        //   }
        //   return true;
        // });
        // if (!matches_all) {
        //   // is_match = false;
        // }

        return is_match;
      });

    if (!isServer) {
      if (newVariant?.attributes?.sku) {
        window.history.pushState(
          "",
          "",
          "/" +
            language +
            "/i/" +
            shopProductSlug +
            "/?variant=" +
            newVariant?.attributes?.sku
        );
      } else {
        window.history.pushState(
          "",
          "",
          "/" + language + "/i/" + shopProductSlug + "/"
        );
      }
    }
    // search for new variant
    setSelectedOptions(newOptions);
    setSelectedVariant(newVariant);
  };

  const resetForm = (skipResetCart) => {
    if (!skipResetCart) {
      dispatch(resetCartState());
      DataLayerHelper.addEventWithOrigin(
        "behaviour",
        "text editor",
        "resetForm"
      );
    }
  };

  /**
   * renders the shopProduct data
   */
  var slugProductShown =
    shopProductDataAttributes &&
    shopProductDataAttributes.slug === shopProductSlug;

  let breadcrumbs = [];

  let currentVariant;
  let volumePrices;
  // Find current variant and breadcrump
  if (shopProductData && shopProductIncluded) {
    currentVariant =
      selectedVariant ||
      defaultVariant ||
      shopProductIncluded?.find((obj) => {
        return (
          obj.id === shopProductDataRelationships.default_variant.data.id &&
          obj.type === "variant"
        );
      });

    const filterAndSortTaxons = (data, taxonType) => {
      return data
        .filter(
          (obj) =>
            obj.type === "taxon" &&
            obj.relationships.taxonomy.data.id === taxonType
        )
        .sort((a, b) => b.attributes.depth - a.attributes.depth);
    };

    // Start Breadcrumb Builder
    let validTaxons = filterAndSortTaxons(shopProductIncluded, "9");
    if (validTaxons.length === 0) {
      validTaxons = filterAndSortTaxons(shopProductIncluded, "1");
    }

    // const getBreadcrumb = (taxon) => ({
    //   title: taxon.attributes.name,
    //   link: `/${language}/${
    //     taxon.relationships.taxonomy.data.id === "1" ? "c" : "t"
    //   }/${taxon.attributes.permalink.replace(
    //     taxon.relationships.taxonomy.data.id === "1" ? "categories/" : "shop/",
    //     ""
    //   )}/`,
    // });

    let currentTaxon = validTaxons[0];
    const breadcrumpChunks = currentTaxon?.attributes?.permalink?.split("/");
    const breadcrumpTitles =
      currentTaxon?.attributes?.pretty_name?.split(" -> ");
    breadcrumpChunks?.shift();
    breadcrumpTitles?.shift();
    if (currentTaxon?.attributes?.hide_from_category_page) {
      breadcrumpChunks?.pop();
      breadcrumpTitles?.pop();
    }
    while (breadcrumpChunks?.length > 0) {
      breadcrumbs.push({
        title: breadcrumpTitles.pop(),
        link: `/${language}/${
          currentTaxon?.relationships?.taxonomy?.data?.id === "1" ? "c" : "t"
        }/${breadcrumpChunks?.join("/")}/`,
      });
      breadcrumpChunks?.pop();
    }

    breadcrumbs.reverse();

    volumePrices = shopProductIncluded
      ?.filter((obj) => obj.type === "model_volume_price")
      .sort((a, b) => b.attributes.position - a.attributes.position);
  }

  // metadata builder
  const title =
    shopProductDataAttributes?.meta_title ||
    shopProductDataAttributes?.name +
      " " +
      shopProductDataAttributes?.subtitle ||
    "";
  const description =
    shopProductDataAttributes?.meta_description ||
    shopProductDataAttributes?.meta_description ||
    shopProductDataAttributes?.short_description ||
    "";
  const published = shopProductDataAttributes?.updated_at;
  const keywords = shopProductDataAttributes?.meta_keywords;

  const image = shopProductIncluded
    ? REACT_APP_SHOP_CDN_SERVER +
      shopProductIncluded.filter((incl) => {
        return (
          incl.type === "image" &&
          incl.id === currentVariant?.relationships?.images?.data[0]?.id
        );
      })[0]?.attributes?.styles[9]?.url
    : "";

  const dataLayer =
    shopProductDataAttributes && currentVariant
      ? [
          {
            ecommerce: {
              detail: {
                products: [
                  {
                    name: title,
                    id: currentVariant?.attributes?.sku,
                    price: currentVariant?.attributes?.price,
                    brand: "myfolie",
                    category: shopProductDataAttributes?.product_type,
                  },
                ],
              },
            },
          },
          {
            event: "view_item",
            value: currentVariant?.attributes?.price,
            items: [
              {
                name: title,
                id: currentVariant?.attributes?.sku,
                price: currentVariant?.attributes?.price,
                brand: "myfolie",
                category: shopProductDataAttributes?.product_type,
                google_business_vertical: "retail",
              },
            ],
          },
        ]
      : null;

  const jsonLd =
    shopProductDataAttributes && currentVariant
      ? {
          "@context": "http://schema.org",
          "@type": "Product",
          url: REACT_APP_WEB_SERVER + props.location.pathname,
          name: title,
          image: [image],
          description: description
            .replace(/<[^>]*>/g, "")
            .replace(/(\r\n|\n|\r)/gm, " "),
          sku: currentVariant?.attributes?.sku,
          aggregateRating: {
            "@type": "AggregateRating",
            bestRating: 5,
            worstRating: 1,
            ratingValue: 4.9,
            reviewCount: 1420,
          },
          brand: {
            "@type": "Organization",
            name: "myfolie GmbH",
          },
          offers: {
            "@type": "Offer",
            url: REACT_APP_WEB_SERVER + props.location.pathname,
            priceCurrency: "EUR",
            price: currentVariant?.attributes?.price,
            availability: "https://schema.org/InStock",
            seller: {
              "@type": "Organization",
              name: "myfolie GmbH",
            },
          },
        }
      : null;

  const expressDeliveryDays =
    shopProductDataAttributes?.express_delivery_days || 4;
  const standardDeliveryDays =
    shopProductDataAttributes?.standard_delivery_days || 8;

  return showFourOFour ? (
    <Status code="404" redirect={errorRedirect}>
      <FourOFour />
    </Status>
  ) : (
    <Page
      id="shopproduct"
      className="shopproduct"
      title={slugProductShown ? title : null}
      description={slugProductShown ? description : null}
      published={slugProductShown ? published : null}
      image={slugProductShown ? image : null}
      keywords={slugProductShown ? keywords : null}
      lang={language}
      pageType="product"
      dataLayer={slugProductShown ? dataLayer : null}
      pageJsonLd={slugProductShown ? jsonLd : null}
    >
      <BreadcrumbBar
        items={breadcrumbs}
        current={
          shopProductDataAttributes
            ? { title: shopProductDataAttributes?.name }
            : false
        }
      />
      {/** If wrong or no product is loaded from api or dictionary and we are loading */}
      {loadingShow && (!slugProductShown || !shopProductDataAttributes) && (
        <LoadingContent layout="product" />
      )}

      {/** START Product Header */}

      {slugProductShown && shopProductData && (
        <Container>
          <Row className="mb-3">
            <ShopProductImageDisplay
              productId={shopProductData?.id}
              currentVariant={currentVariant}
            />
            <Col md={7}>
              <h1 className="h4 pb-0 mt-1" ref={buyBoxRef}>
                {shopProductDataAttributes.name}
                {shopProductDataAttributes.subtitle && (
                  <span className="h5 d-block m-0">
                    {shopProductDataAttributes.subtitle}
                  </span>
                )}
              </h1>

              {shopProductIncluded?.find((obj) => {
                return (
                  obj.type === "option_type" &&
                  (obj.attributes.name === "custom_size" ||
                    obj.attributes.name === "custom_colorselect")
                );
              }) ? (
                <ProductCustomization
                  productId={shopProductData?.id}
                  custom_size={custom_size}
                  handleMaterialChange={handleMaterialChange}
                  handleColorChange={handleColorChange}
                  selectedColor={selectedColor}
                  addToCartProcessing={addToCartProcessing}
                  handleCustomSizeChange={handleCustomSizeChange}
                  materialSerie={materialSerie}
                />
              ) : (
                ""
              )}

              <ProductVariantSelector
                productId={shopProductData?.id}
                currentVariant={currentVariant}
                selectedOptions={selectedOptions}
                changeProductOption={changeProductOption}
              />

              {!shopProductIncluded && (
                <div
                  className="sk-loading btn btn-block mt-4 sk-bg300"
                  style={{ height: "40px" }}
                />
              )}

              <PurchaseButton
                configuratorPage={configuratorPage}
                productId={shopProductData?.id}
                currentVariant={currentVariant}
                amount={amount}
                updateAmount={updateAmount}
                addCustomItemToCart={addCustomItemToCart}
                handleAddItemToCart={handleAddItemToCart}
                scrollToConfigurator={scrollToConfigurator}
                expressDeliveryDays={expressDeliveryDays}
                standardDeliveryDays={standardDeliveryDays}
                skipdc={skipdc}
                getCustomPrice={getCustomPrice}
                scrollToSection={scrollToSection}
              />

              {/** SPECIAL: Blank Film Cuts */}
              {shopProductDataAttributes.product_type.includes(
                "Klebefolie Laufmeter"
              ) && (
                <>
                  <MoreColorsTeaser
                    productType={shopProductDataAttributes.product_type}
                  />
                  <Link
                    to={`/${language}/folienzuschnitt/${
                      shopProductDataAttributes.mpt_material_id
                        ? `?vinyl=${shopProductDataAttributes.mpt_material_id}`
                        : ``
                    }`}
                    className="d-flex align-items-center mt-3"
                  >
                    <img
                      src="https://cdn.myfolie.de/media/individualcut-nav.png"
                      height="46"
                      width="108"
                      alt={context.t("Foil cutting service")}
                    />
                    <p className="bg-light py-2 px-3 mb-0 ml-2">
                      <strong>
                        {context.t("NEW")}: {context.t("Foil cutting service")}
                      </strong>
                      <br />
                      {context.t("We cut the films to your desired format!")}
                    </p>
                  </Link>
                </>
              )}
            </Col>
          </Row>
        </Container>
      )}
      {/** END Product Header */}

      {slugProductShown && shopProductData && configuratorPage && (
        <div
          ref={inpageConfiguratorRef}
          style={{ scrollMargin: "2rem" }}
          className="my-3 py-5 bg-light"
        >
          <Container>
            <h2 className="h4">
              {context.t("Configure your individual")}{" "}
              {shopProductDataAttributes?.name}
            </h2>
          </Container>
          <InproductConfigurator
            productTitle={shopProductDataAttributes.name}
            variantId={currentVariant?.id}
            scrollToConfigurator={scrollToConfigurator}
            editorTypeId={
              shopProductDataAttributes.myfolie_configurator_id || 1
            }
          />
        </div>
      )}

      {/** START Content Section */}
      {slugProductShown && shopProductData && (
        <Sticky className="product-subnav sticky--wrapper" topOffset={-65}>
          <Container>
            <Nav>
              <NavItem
                onClick={() => scrollToSection("description")}
                className={activeSection === "description" ? "active" : ""}
              >
                {context.t("Details")}
              </NavItem>
              {shopProductCmsBlocks &&
                shopProductCmsBlocks
                  .split(",")
                  .some((cms_block) =>
                    cms_block.startsWith("tab_installation")
                  ) && (
                  <NavItem
                    onClick={() => scrollToSection("installation")}
                    className={activeSection === "installation" ? "active" : ""}
                  >
                    {context.t("Installation")}
                  </NavItem>
                )}
              {shopProductCmsBlocks &&
                shopProductCmsBlocks
                  .split(",")
                  .some((cms_block) =>
                    cms_block.startsWith("tab_material")
                  ) && (
                  <NavItem
                    onClick={() => scrollToSection("material")}
                    className={activeSection === "material" ? "active" : ""}
                  >
                    {context.t("Material & Finish")}
                  </NavItem>
                )}
              <NavItem
                onClick={() => scrollToSection("specification")}
                className={activeSection === "specification" ? "active" : ""}
              >
                {context.t("Technical Specifications")}
              </NavItem>
              {shopProductFaqSlugs && faqDictionary && (
                <NavItem
                  onClick={() => scrollToSection("faq")}
                  className={activeSection === "faq" ? "active" : ""}
                >
                  {context.t("FAQs")}
                </NavItem>
              )}
              {configuratorPage && (
                <NavItem onClick={() => scrollToConfigurator()}>
                  <span
                    className="btn btn-sm btn-primary"
                    style={{ marginTop: "-3px" }}
                  >
                    {context.t("configure now")}
                  </span>
                </NavItem>
              )}

              {!configuratorPage &&
                shopProductDataAttributes?.customizable &&
                parseInt(shopProductDataAttributes.myfolie_configurator_id) !==
                  6 && (
                  <NavItem>
                    <Link
                      className="btn btn-sm btn-primary"
                      style={{ marginTop: "-3px" }}
                      to={`/${language}/eigenes-motiv/?product=${
                        shopProductDataAttributes.myfolie_configurator_id
                      }&amount=${
                        [18, 19, 20].includes(
                          parseInt(
                            shopProductDataAttributes.myfolie_configurator_id
                          )
                        )
                          ? "50&width=25&height=25"
                          : amount
                      }${skipdc ? "&skipdc=true" : ""}`}
                    >
                      {context.t("Customize")}
                    </Link>
                  </NavItem>
                )}
              {!configuratorPage &&
                shopProductDataAttributes?.customizable &&
                parseInt(shopProductDataAttributes.myfolie_configurator_id) ===
                  6 && (
                  <NavItem>
                    <Link
                      className="btn btn-sm btn-primary"
                      style={{ marginTop: "-3px" }}
                      to={`/${language}/klebeschriften/?amount=${amount}`}
                    >
                      {context.t("Customize")}
                    </Link>
                  </NavItem>
                )}
              {!configuratorPage &&
                !shopProductDataAttributes?.customizable && (
                  <NavItem onClick={() => scrollToBuySection()}>
                    <span
                      className="btn btn-sm btn-primary"
                      style={{ marginTop: "-3px" }}
                    >
                      {context.t("Buy Now")}
                    </span>
                  </NavItem>
                )}
            </Nav>
          </Container>
        </Sticky>
      )}

      {/** CONTENT / DESCRIPTION */}
      {slugProductShown && shopProductData && (
        <Container>
          {shopProductDataAttributes.description && (
            <div ref={descriptionRef} style={{ scrollMargin: "6.5rem" }}>
              <Row className="my-3">
                <Col sm={12} md={{ size: 8 }} className="py-2">
                  <h2 className="h4">
                    {context.t("More Details About")}{" "}
                    {shopProductDataAttributes.name}
                  </h2>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: shopProductDataAttributes.description,
                    }}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Container>
      )}

      {/** VOLUME PRICES & SHIPPING OPTIONS */}
      {slugProductShown && shopProductData && (
        <Container>
          <Row className="bg-light py-3 my-3">
            {volumePrices?.length > 0 && (
              <Col md={6}>
                <VolumeDiscountTable volumePrices={volumePrices} />
              </Col>
            )}
            <Col md={6}>
              <ShippingOptions
                selectedShippingCountry={selectedShippingCountry}
                setSelectedShippingCountry={setSelectedShippingCountry}
                updateAmount={updateAmount}
                amount={amount}
                expressDeliveryDays={expressDeliveryDays}
                standardDeliveryDays={standardDeliveryDays}
                isCustomizeable={
                  configuratorPage || shopProductDataAttributes.customizable
                }
              />
            </Col>
          </Row>
        </Container>
      )}

      {/** CONTENT BLOCKS */}
      {shopProductCmsBlocks &&
        shopProductCmsBlocks?.split(",").map((cms_block, idx) => {
          const cms_block_data = availableCmsBlocks.filter(
            (cmsBlock) => cmsBlock.slug === cms_block
          );

          if (cms_block_data && cms_block_data[0]) {
            const cmsBlock = cms_block_data[0];

            return (
              <Container key={"shopblock-" + idx} className="my-5">
                {cmsBlock.slug.startsWith("tab_material") ? (
                  <div ref={materialRef} style={{ scrollMargin: "6.5rem" }}>
                    <CMSBlock cmsBlock={cmsBlock} />
                  </div>
                ) : cmsBlock.slug.startsWith("tab_installation") ? (
                  <div ref={installationRef} style={{ scrollMargin: "6.5rem" }}>
                    <CMSBlock cmsBlock={cmsBlock} />
                  </div>
                ) : (
                  <CMSBlock cmsBlock={cmsBlock} />
                )}
              </Container>
            );
          }

          return null;
        })}

      {/** Technical Specifications */}
      <Container>
        {shopProductIncluded &&
          shopProductDataRelationships.product_properties.data.length > 0 && (
            <div ref={specificationRef} style={{ scrollMargin: "6.5rem" }}>
              <Row className="my-3">
                <Col sm="12" className="py-2">
                  <h2 className="h4">
                    {context.t("Technical Specifications")}
                  </h2>
                  <Table striped>
                    <tbody>
                      <tr>
                        <th scope="row">{context.t("SKU")}:</th>
                        <td>{currentVariant.attributes.sku}</td>
                      </tr>
                      {shopProductDataRelationships.product_properties.data.map(
                        (prop, idx) => {
                          const property = shopProductIncluded.find((obj) => {
                            return (
                              obj.id === prop.id &&
                              obj.type === "product_property"
                            );
                          });
                          return (
                            <React.Fragment key={idx}>
                              {property &&
                                property.attributes &&
                                property.attributes.value && (
                                  <tr key={property.attributes.name}>
                                    <th scope="row">
                                      {property.attributes.description}
                                    </th>
                                    <td
                                      dangerouslySetInnerHTML={{
                                        __html: property.attributes.value,
                                      }}
                                    />
                                  </tr>
                                )}
                            </React.Fragment>
                          );
                        }
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </div>
          )}

        {/** FAQs */}
        {shopProductFaqSlugs && faqDictionary && (
          <div ref={faqRef} style={{ scrollMargin: "6.5rem" }}>
            <Row>
              <Col sm="12" md={{ size: 8, offset: 2 }} className="py-2">
                <h2 className="h4 text-center">
                  {context.t("Frequent asked questions on")}{" "}
                  {shopProductDataAttributes?.name}
                </h2>
                <FaqsAccordion
                  faqDictionary={faqDictionary}
                  slugs={shopProductFaqSlugs}
                  language={language}
                />
              </Col>
            </Row>
          </div>
        )}
      </Container>

      {/** RELATED PRODUCTS */}
      {shopProductDataAttributes?.related_products?.length > 0 && (
        <RelatedProducts
          relatedProducts={shopProductDataAttributes?.related_products}
        />
      )}

      {(addToShopCartSuccess || addToCartProcessing) && !configuratorPage && (
        <AddToCartModal
          addItemToCart={(options) => dispatch(addItemToCart(options))}
          productCount={cart?.data?.attributes?.item_count}
          language={language}
          // FIXME maybe not the best place to do, but okay for production
          extraProducts={
            cart?.included?.filter((item) => {
              return (
                item?.type === "line_item" && item?.attributes?.sku === "7004"
              );
            }).length > 0
              ? ["text", "print"]
              : ["text", "print", "squeegee"]
          }
          ctaTitle={context.t("Add a different product")}
          addingToCart={addToCartProcessing} // FIXME should be creating product
          addedToCart={addToShopCartSuccess} // FIXME should be creating product
          navigateTo={(url) => dispatch(push(url))}
          addToCartError={addToCartError}
          addToShopCartSuccess={addToShopCartSuccess}
          addToCartProcessing={addToCartProcessing}
          ctaAction={resetForm}
          resetCart={() => dispatch(resetCartState())}
        />
      )}

      <Modal
        isOpen={!!cookieError}
        toggle={resetCookieError}
        fade={false}
        centered
      >
        <ModalHeader toggle={resetCookieError}>
          {context.t("Alert")}
        </ModalHeader>
        <ModalBody>
          <div>
            <div className="d-flex justify-content-center mb-3">
              <FaTimes className="fa-fw fa-5x text-danger" aria-hidden="true" />
            </div>
            <p>
              {cookieError &&
                context.t(
                  "Cookies are disabled in your browser. Please enable mandatory cookies to add an item to your cart."
                )}
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={resetCookieError}>
            {context.t("Close")}
          </Button>{" "}
        </ModalFooter>
      </Modal>
    </Page>
  );
};

/**  define proptype for the 'translation' function  */
ShopProduct.contextTypes = {
  t: PropTypes.func,
};

/** export the redux connected component and add a frontload call */
export default ShopProduct;
